import type { IntegrationJob } from "../../shared/types";

interface ParsedCron {
  seconds: number[];
  minutes: number[];
  hours: number[];
  daysOfMonth: number[];
  months: number[];
  daysOfWeek: number[];
  dayOfMonthWildcard: boolean;
  dayOfWeekWildcard: boolean;
}

const monthAliases: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12
};

const dayAliases: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6
};

function parseToken(value: string, aliases: Record<string, number>): number | undefined {
  const normalized = value.trim().toUpperCase();
  if (normalized in aliases) {
    return aliases[normalized];
  }

  const parsed = Number(normalized);
  return Number.isInteger(parsed) ? parsed : undefined;
}

function expandRange(start: number, end: number, step: number): number[] {
  const values: number[] = [];
  for (let current = start; current <= end; current += step) {
    values.push(current);
  }
  return values;
}

function parseField(
  field: string | undefined,
  min: number,
  max: number,
  aliases: Record<string, number> = {}
): number[] | undefined {
  if (!field || field === "*" || field === "?") {
    return expandRange(min, max, 1);
  }

  const values = new Set<number>();
  for (const rawPart of field.split(",")) {
    const part = rawPart.trim();
    if (!part) {
      continue;
    }

    const [baseRaw, stepRaw] = part.split("/");
    const step = stepRaw === undefined ? 1 : Number(stepRaw);
    if (!Number.isInteger(step) || step <= 0) {
      return undefined;
    }

    const base = baseRaw || "*";
    let start: number | undefined;
    let end: number | undefined;

    if (base === "*" || base === "?") {
      start = min;
      end = max;
    } else if (base.includes("-")) {
      const [startRaw, endRaw] = base.split("-");
      start = parseToken(startRaw, aliases);
      end = parseToken(endRaw, aliases);
    } else {
      start = parseToken(base, aliases);
      end = stepRaw === undefined ? start : max;
    }

    if (start === undefined || end === undefined || start < min || end > max || start > end) {
      return undefined;
    }

    for (const value of expandRange(start, end, step)) {
      if (value >= min && value <= max) {
        values.add(value);
      }
    }
  }

  return values.size > 0 ? [...values].sort((left, right) => left - right) : undefined;
}

function isWildcard(field: string | undefined): boolean {
  return !field || field === "*" || field === "?";
}

function parseCronExpression(cronExpression: string): ParsedCron | undefined {
  const fields = cronExpression.trim().split(/\s+/);
  if (fields.length !== 5 && fields.length !== 6) {
    return undefined;
  }

  const [secondField, minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] = fields.length === 6
    ? fields
    : ["0", fields[0], fields[1], fields[2], fields[3], fields[4]];

  const seconds = parseField(secondField, 0, 59);
  const minutes = parseField(minuteField, 0, 59);
  const hours = parseField(hourField, 0, 23);
  const daysOfMonth = parseField(dayOfMonthField, 1, 31);
  const months = parseField(monthField, 1, 12, monthAliases);
  const daysOfWeek = parseField(dayOfWeekField, 0, 7, dayAliases);

  if (!seconds || !minutes || !hours || !daysOfMonth || !months || !daysOfWeek) {
    return undefined;
  }

  return {
    seconds,
    minutes,
    hours,
    daysOfMonth,
    months,
    daysOfWeek,
    dayOfMonthWildcard: isWildcard(dayOfMonthField),
    dayOfWeekWildcard: isWildcard(dayOfWeekField)
  };
}

function dateMatches(parsed: ParsedCron, date: Date): boolean {
  const utcDay = date.getUTCDate();
  const utcMonth = date.getUTCMonth() + 1;
  const utcDayOfWeek = date.getUTCDay();
  const monthMatches = parsed.months.includes(utcMonth);
  const dayOfMonthMatches = parsed.daysOfMonth.includes(utcDay);
  const dayOfWeekMatches = parsed.daysOfWeek.includes(utcDayOfWeek) || (utcDayOfWeek === 0 && parsed.daysOfWeek.includes(7));

  if (!monthMatches) {
    return false;
  }

  if (parsed.dayOfMonthWildcard && parsed.dayOfWeekWildcard) {
    return true;
  }
  if (parsed.dayOfMonthWildcard) {
    return dayOfWeekMatches;
  }
  if (parsed.dayOfWeekWildcard) {
    return dayOfMonthMatches;
  }

  return dayOfMonthMatches || dayOfWeekMatches;
}

export function expectedStartForDate(job: IntegrationJob, date: Date): string {
  if (job.expectedStartTime.includes("T")) {
    return new Date(job.expectedStartTime).toISOString();
  }

  const [hour = "00", minute = "00", second = "00"] = job.expectedStartTime.split(":");
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    Number(hour),
    Number(minute),
    Number(second)
  )).toISOString();
}

export function scheduleSlotKey(jobId: string, expectedStartTime: string): string {
  const slotKey = new Date(expectedStartTime).toISOString().replace(/\D/g, "").slice(0, 14);
  return `${jobId}|${slotKey}`;
}

export function getScheduleOccurrencesForDate(job: IntegrationJob, date: Date): string[] {
  if (!job.cronExpression) {
    return [expectedStartForDate(job, date)];
  }

  const parsed = parseCronExpression(job.cronExpression);
  if (!parsed) {
    return [];
  }

  if (!dateMatches(parsed, date)) {
    return [];
  }

  const occurrences: string[] = [];
  for (const hour of parsed.hours) {
    for (const minute of parsed.minutes) {
      for (const second of parsed.seconds) {
        occurrences.push(new Date(Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          hour,
          minute,
          second
        )).toISOString());
      }
    }
  }

  return occurrences.sort();
}
